/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation, useTranslation } from 'react-i18next'
// core components
import Header from '../components/Headers/Header'
import { getRestaurantProfile, updateTimings } from '../apollo'
import TimeRangePicker from '@wojtekmaj/react-timerange-picker'
import CustomLoader from '../components/Loader/CustomLoader'
import useGlobalStyles from '../utils/globalStyles'
import {
  Container,
  Box,
  Button,
  Alert,
  IconButton,
  Switch,
  Typography
} from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import RemoveCircleOutlineRounded from '@mui/icons-material/RemoveCircleOutlineRounded'
import ScheduleRounded from '@mui/icons-material/ScheduleRounded'
import { SectionCard, raisedButtonSx } from '../components/UI/Depth'
import { toneTile } from '../utils/depth'
const GET_RESTAURANT_PROFILE = gql`
  ${getRestaurantProfile}
`
const UPDATE_TIMINGS = gql`
  ${updateTimings}
`
const Timings = props => {
  const [value, onChange] = useState({})
  const restaurantId = localStorage.getItem('restaurantId')

  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const { t } = props
  const onChangeTime = (day, values) => {
    value[day] = values
    onChange(value)
  }
  const getTransformedTimings = e => {
    const openingTimes = Object.keys(value).map(v => {
      return {
        day: v,
        times: value[v].map(timings => ({
          startTime: timings[0].split(':'),
          endTime: timings[1].split(':')
        }))
      }
    })
    return openingTimes
  }
  const { data, error: errorQuery, loading: loadingQuery } = useQuery(
    GET_RESTAURANT_PROFILE,
    {
      variables: { id: restaurantId }
    }
  )
  const transformedTimes = {}

  const [mutate, { loading }] = useMutation(UPDATE_TIMINGS)

  data &&
    data.restaurant.openingTimes.forEach(value => {
      transformedTimes[value.day] = value.times.map(t => [
        `${t.startTime[0]}:${t.startTime[1]}`,
        `${t.endTime[0]}:${t.endTime[1]}`
      ])
    })
  const globalClasses = useGlobalStyles()

  return (
    <>
      <Header />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {errorQuery ? <span>Error! {errorQuery.message}</span> : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <SectionCard
            icon={ScheduleRounded}
            title={t('OpenTimes')}
            subtitle={t('TimingsHelp')}
            bodySx={{ pt: 2.5 }}>
            {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(day => (
              <DayComponent
                key={day}
                day={t(day)}
                value={transformedTimes[day] || [['00:00', '23:59']]}
                onChangeTime={onChangeTime}
              />
            ))}
            <Button
              onClick={e => {
                e.preventDefault()
                const openingTimes = getTransformedTimings()
                mutate({
                  variables: {
                    id: restaurantId,
                    openingTimes
                  },
                  onCompleted: () => {
                    setSuccessMessage(t('TimeSavedSuccessfully'))
                    setTimeout(() => setSuccessMessage(''), 5000)
                    setErrorMessage('')
                  },
                  onError: error => {
                    setErrorMessage(t('ErrorWhileSavingTime'))
                    setTimeout(() => setErrorMessage(''), 5000)
                    setSuccessMessage('')
                  }
                })
              }}
              disabled={loading}
              sx={{ ...raisedButtonSx, mt: 1, mb: 2, minWidth: 140 }}>
              {loading ? t('SavingDots') : t('Save')}
            </Button>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </SectionCard>
        )}
      </Container>
    </>
  )
}

export default withTranslation()(Timings)
const DayComponent = ({ day, value, onChangeTime }) => {
  const { t } = useTranslation()
  useEffect(() => {
    onChangeTime(day, values)
  })
  const [values, onChange] = useState(value)
  const isOpen = values.length > 0
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 1.75,
        mb: 1.5,
        borderRadius: '14px',
        border: '1px solid rgba(16,24,40,0.07)',
        background: isOpen
          ? 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFB 100%)'
          : '#F6F7F9',
        boxShadow: isOpen
          ? 'inset 0 1px 0 #fff, 0 4px 12px -8px rgba(16,24,40,0.25)'
          : 'inset 0 1px 3px rgba(16,24,40,0.06)'
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: 170 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.04em',
            ...(isOpen
              ? toneTile('orange')
              : { bgcolor: 'grey.200', color: 'text.disabled' })
          }}>
          {String(day).slice(0, 3)}
        </Box>
        <Box>
          <Switch
            size="small"
            checked={isOpen}
            onChange={e =>
              onChange(e.target.checked ? [['00:00', '23:59']] : [])
            }
          />
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: isOpen ? 'success.main' : 'text.disabled'
            }}>
            {isOpen ? t('Open') : t('ClosedAllDay')}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.25,
          flexGrow: 1,
          '& .react-timerange-picker__wrapper': {
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            padding: '6px 10px',
            backgroundColor: '#FAFAFB',
            boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.07)'
          }
        }}>
        {values.map((value, index) => (
          <Box
            key={value + index}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeRangePicker
              required
              rangeDivider="to"
              disableClock
              format="hh:mm a"
              clearIcon={null}
              value={value}
              onChange={value => {
                values[index] = value
                onChange([...values])
              }}
            />
            {values.length > 1 && (
              <IconButton
                size="small"
                aria-label={t('Remove')}
                onClick={e => {
                  e.preventDefault()
                  values.splice(index, 1)
                  onChange([...values])
                }}
                sx={{
                  color: 'text.disabled',
                  '&:hover': { color: 'error.main' }
                }}>
                <RemoveCircleOutlineRounded fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        {isOpen ? (
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={e => {
              e.preventDefault()
              onChange([...values, ['00:00', '23:59']])
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px'
            }}>
            {t('AddTimeSlot')}
          </Button>
        ) : (
          <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
            {t('ClosedAllDay')}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
